/**
 * Answers already fetched, kept across navigation.
 *
 * Every page threw its data away when it unmounted, so moving from the bills
 * list into a bill and back fetched the whole list again - and the analytics
 * page, which makes six requests, paid for all six every time somebody came
 * back to it. The data had not changed; the component had.
 *
 * Kept deliberately small and dumb:
 *
 * * **it is not a store.** Nothing reads through it to find out what is true.
 *   It hands back an answer that was correct a moment ago so the screen has
 *   something to draw, and a fresh request is made anyway;
 * * **anything that changes data empties it.** Approving a bill, saving a
 *   correction, merging two dishes - every non-GET clears the lot, from inside
 *   the HTTP layer so no caller has to remember to. Blanket rather than
 *   targeted, because working out which answers a write invalidates is a
 *   guess, and the cost of guessing wrong is showing somebody a bill as
 *   unapproved after they approved it. Clearing everything costs one refetch;
 *   guessing wrong costs trust in the screen;
 * * **it expires.** A tab left open for an hour must not draw hour-old figures
 *   even for the instant before the refresh lands.
 */

// How long an answer may still be shown while a fresh one is fetched. Short
// enough that anything stale is visibly replaced within a moment, long enough
// to cover going into a record and coming back out.
const GOOD_FOR_MS = 5 * 60 * 1000;

const entries = new Map();

/** The answer for this key, or undefined if there is none worth showing. */
export function read(key) {
    const hit = entries.get(key);
    if (!hit) return undefined;
    if (Date.now() - hit.at > GOOD_FOR_MS) {
        entries.delete(key);
        return undefined;
    }
    return hit.data;
}

export function write(key, data) {
    entries.set(key, { data, at: Date.now() });
}

/**
 * Forget everything. Called from the HTTP layer on every write, and on sign-out
 * - one account's figures must never be the first thing the next one sees.
 */
export function clear() {
    entries.clear();
}

/** Only for tests, which need to know whether a request was avoided. */
export function size() {
    return entries.size;
}
